import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Box,
  Alert,
  IconButton,
  CircularProgress,
} from '@mui/material'
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material'

interface ExportProgressDialogProps {
  open: boolean
  onClose: () => void
  onExport: (
    onProgress: (progress: number, status: string) => void,
    onError: (error: string) => void,
  ) => Promise<void>
  surveyName: string
}

export default function ExportProgressDialog({
  open,
  onClose,
  onExport,
  surveyName,
}: ExportProgressDialogProps) {
  const [progress, setProgress] = useState(0)
  const [status, setStatus] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)

  useEffect(() => {
    if (open && !isExporting && !isCompleted) {
      handleExport()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleExport = async () => {
    setIsExporting(true)
    setError(null)
    setProgress(0)
    setStatus('Starting export...')

    try {
      await onExport(
        (progressValue, statusMessage) => {
          setProgress(progressValue)
          setStatus(statusMessage)
        },
        errorMessage => {
          setError(errorMessage)
          setIsExporting(false)
        },
      )

      setIsCompleted(true)
      setIsExporting(false)
      setProgress(100)
      setStatus('Export completed successfully!')

      setTimeout(() => {
        handleClose()
      }, 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed')
      setIsExporting(false)
    }
  }

  const handleClose = () => {
    if (!isExporting) {
      setProgress(0)
      setStatus('')
      setError(null)
      setIsCompleted(false)
      setIsExporting(false)
      onClose()
    }
  }

  const handleRetry = () => {
    setError(null)
    setIsCompleted(false)
    handleExport()
  }

  const getProgressColor = () => {
    if (error) return 'error'
    if (isCompleted) return 'success'
    return 'primary'
  }

  const getStatusIcon = () => {
    if (error) return <ErrorIcon color='error' />
    if (isCompleted) return <CheckCircleIcon color='success' />
    return <CircularProgress size={20} color='primary' />
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      disableEscapeKeyDown={isExporting}
    >
      <DialogTitle>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Typography variant='h6'>Export Survey Data</Typography>
          {!isExporting && (
            <IconButton onClick={handleClose} size='small'>
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box mb={2}>
          <Typography variant='body2' color='textSecondary' gutterBottom>
            Survey: {surveyName}
          </Typography>
        </Box>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box mb={2}>
          <Box display='flex' alignItems='center' mb={1}>
            {getStatusIcon()}
            <Typography variant='body2' sx={{ ml: 1 }}>
              {status}
            </Typography>
          </Box>

          <LinearProgress
            variant='determinate'
            value={progress}
            color={getProgressColor()}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        {isExporting && (
          <Typography variant='body2' color='textSecondary'>
            This may take a few minutes for large surveys. Please don't close
            this window.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        {error && (
          <Button onClick={handleRetry} variant='contained' color='primary'>
            Retry
          </Button>
        )}

        {!isExporting && (
          <Button onClick={handleClose} variant='outlined'>
            {isCompleted ? 'Close' : 'Cancel'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
